import { type SetStateAction, useCallback, useEffect, useState } from "react";

/**
 * Sync a piece of state with a URL query parameter.
 *
 * - `parse` converts the string param to `T | null` (null = use default / remove param).
 * - `serialize` converts `T` back to a string for the URL.
 * - When value is `null`, the param is removed from the URL.
 * - Uses `replaceState` to avoid polluting browser history.
 */
export function useQueryParam<T>(
  key: string,
  parse: (raw: string) => T | null,
  serialize: (value: T) => string,
): [T | null, (value: SetStateAction<T | null>) => void] {
  const [value, setValueRaw] = useState<T | null>(() => {
    const raw = new URLSearchParams(window.location.search).get(key);
    return raw != null ? parse(raw) : null;
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    if (value != null) {
      url.searchParams.set(key, serialize(value));
    } else {
      url.searchParams.delete(key);
    }
    if (url.href !== window.location.href) {
      window.history.replaceState(null, "", url);
    }
  }, [key, value, serialize]);

  const setValue = useCallback((v: SetStateAction<T | null>) => {
    setValueRaw(v);
  }, []);

  return [value, setValue];
}
